import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { ClearOutlined, Delete, Edit, Visibility } from "@mui/icons-material";
import { Box, IconButton, LinearProgress, Tooltip } from "@mui/material";
import { PreviousUpload, Upload, UploadOptions } from "@maysoft/tus-js-client";

import Global from "../../commons/global";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import Resources from "../../constants/resources";
import AssetService from "../../services/asset/asset.service";
import ResumableUploadService from "../../services/asset/resumableUpload.service";

import { Method } from "../../services/base/HttpClient";
import { actionBox, image, previewImageContainer } from "./styles";
import { IAssetInfo, UploadFileResponse } from "../../commons/interfaces";
import { FileAccessMode, FileProcessingStatus, Mode } from "../../constants/enums";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";
import { IHiddenActionImageUploader } from ".";

interface IPreviewFileItem {
    index: number;
    folderPath: string;
    mode?: Mode;
    fileId?: string;
    fileUrl?: string;
    fileBinary?: File;
    startUpload?: boolean;
    accessMode?: FileAccessMode;
    optimize?: boolean;
    errorMessage?: string;
    onUploadDone?: (res: UploadFileResponse) => void;
    onReplace?: () => void;
    onRemove?: () => void;
    onView?: () => void;
    assetInfo: IAssetInfo;
    hiddenAction?: IHiddenActionImageUploader;
}

interface IExtendedPreviousUpload extends PreviousUpload {
    uploadUrl: string;
    urlStorageKey: string;
    parallelUploadUrls?: string[];
}

const PreviewImageItem = (props: IPreviewFileItem) => {
    const strings = useStrings();
    const isMounted = useRef(true);
    const tusUpload = useRef<Upload>();
    const tusUploadUrl = useRef<string>();

    const context = useCommonComponentContext();
    const assetInfo = props.assetInfo;

    // const [assetInfo, setAssetInfo] = useState<IAssetInfo>();
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const uploadProgressRef = useRef(uploadProgress);

    const token = Global.axios.defaults.headers.common["Authorization"].toString();

    const uploadFile = async (file: File) => {
        setErrorMessage("");
        setUploadProgress(0);

        if (Helpers.isNullOrEmpty(token)) {
            handleUploadError();
            return;
        }

        if (file.name.includes(".exe")) {
            setErrorMessage(strings.Common.INVALID_FILE);
        }

        try {
            const accessMode = !Helpers.isNullOrEmpty(props.accessMode) ? `${props.accessMode}` : `${FileAccessMode.Private}`;

            let processingStatus = `${FileProcessingStatus.Unprocessed}`;
            if (props.optimize === false || (!file.type.includes("image") && !file.type.includes("video") && !file.type.includes("openxmlformats"))) {
                processingStatus = `${FileProcessingStatus.Processed}`;
            }

            const request = {
                accessMode: accessMode,
                folderPath: props.folderPath,
                tenantCode: context.tenantCode,
                serviceCode: context.serviceCode,
                processingStatus,
            };

            const options: UploadOptions = {
                onBeforeRequest: (req) => {
                    if (Helpers.isNullOrEmpty(tusUploadUrl.current) && req.getMethod() === Method.PATCH) {
                        tusUploadUrl.current = req.getURL();
                    }
                },
                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = parseInt(((bytesUploaded / bytesTotal) * 100).toFixed(2));
                    setUploadProgress(percentage);
                    uploadProgressRef.current = percentage;
                },
                onSuccess: (res) => {
                    let id = "";
                    if (res.getHeader("asset-id")) {
                        id = res.getHeader("asset-id");
                    }

                    let accessUrl = "";
                    if (res.getHeader("asset-url")) {
                        accessUrl = res.getHeader("asset-url");
                    }

                    let error = "";
                    if (res.getHeader("asset-error")) {
                        error = res.getHeader("asset-error");
                        setErrorMessage(error);
                        handleUploadError();
                    }

                    const response = {
                        id,
                        error,
                        accessUrl,
                    };

                    tusUpload.current = undefined;
                    tusUploadUrl.current = undefined;
                    props.onUploadDone && props.onUploadDone(response);
                    setUploading(false);
                },
                onError: (error) => {
                    handleUploadError();
                },
            };

            tusUpload.current = ResumableUploadService.upload(file, request, options);

            setUploading(true);
            tusUpload.current.start();
        } catch (error) {
            if (tusUpload.current) {
                tusUpload.current.abort();
            }
            handleUploadError();
        }
    };

    const handleUploadError = () => {
        setUploading(false);
        if (Helpers.isNullOrEmpty(errorMessage)) {
            setErrorMessage(strings.Common.UPLOAD_FILE_ERROR);
        }
    };

    const handleRemove = useCallback(() => {
        if (uploading) {
            terminateUpload();
            setUploadProgress(0);
            setUploading(false);
        }
        props.onRemove && props.onRemove();
    }, [uploading, props]);

    useEffect(() => {
        // const init = async () => {
        //     try {
        //         if (!Helpers.isNullOrEmpty(props.fileId) && Helpers.isNullOrEmpty(assetInfo)) {
        //             const assetInfo = await AssetService.getAssetInfo(props.fileId);
        //             setAssetInfo(assetInfo);
        //         }
        //     } catch (error) {
        //         console.log(error);
        //     }
        // };
        // init();
        return () => {
            isMounted.current = false;
        };
    }, [props.fileId]);

    useEffect(() => {
        if (isMounted.current === true && uploadProgressRef.current < 100 && props.startUpload === true && !Helpers.isNullOrEmpty(props.fileBinary)) {
            // console.log("Start upload index:", props.index);
            uploadFile(props.fileBinary);
        }

        return () => {
            isMounted.current = false;
            if (uploadProgressRef.current < 100) {
                terminateUpload();
            }
        };
    }, [props.fileBinary, props.startUpload]);

    const terminateUpload = () => {
        // Abort upload first, then remove upload key in localStorage
        if (tusUpload.current) {
            tusUpload.current.abort();
            tusUpload.current
                .findPreviousUploads()
                .then((allPreviousUploads) => {
                    const totalPreviousUploads = allPreviousUploads.length;
                    for (let index = 0; index < totalPreviousUploads; index++) {
                        const previousUpload = allPreviousUploads[index] as IExtendedPreviousUpload;
                        if (
                            tusUploadUrl.current &&
                            Array.isArray(previousUpload.parallelUploadUrls) &&
                            previousUpload.parallelUploadUrls.indexOf(tusUploadUrl.current) > -1
                        ) {
                            localStorage.removeItem(previousUpload.urlStorageKey);
                            previousUpload.parallelUploadUrls?.forEach((url: string) => {
                                Upload.terminate(url).catch((error) => {
                                    console.log("Error when terminate upload:", error);
                                });
                            });
                            break;
                        }
                    }
                    tusUploadUrl.current = undefined;
                })
                .catch((error) => {
                    console.log("Error when abort upload:", error);
                })
                .finally(() => {
                    tusUploadUrl.current = undefined;
                });
        }
    };

    const stopPropagation = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
    };

    const getImageUrl = useMemo(() => {
        if (assetInfo?.isDocument) return Resources.IMAGES.DOCUMENT;
        if (assetInfo?.isImage && !Helpers.isNullOrEmpty(assetInfo.accessUrl) && assetInfo.id === "0") return assetInfo.accessUrl;
        if (assetInfo?.isImage) return Helpers.isNullOrEmpty(assetInfo?.id) ? Resources.IMAGES.THUMBNAIL : Helpers.getFileAccessUrl(assetInfo?.id);
        return Resources.IMAGES.THUMBNAIL;
    }, [assetInfo?.isDocument, assetInfo?.isImage, assetInfo?.isVideo, assetInfo?.id]);

    return (
        <Box sx={previewImageContainer} onClick={stopPropagation}>
            {/* File info */}
            <Box component={"img"} src={getImageUrl} sx={image} alt="uploader" />
            <Box sx={actionBox}>
                {(props.fileId || props.fileUrl) && (
                    <>
                        {props.mode !== Mode.View && !props.hiddenAction?.replace && (
                            <IconButton
                                sx={{ p: 2 }}
                                onClick={(e) => {
                                    stopPropagation(e as any);
                                    props.onReplace();
                                }}
                            >
                                <Tooltip title={strings.Common.CHANGE} placement="top">
                                    <Edit sx={{ color: "#ffffff" }} />
                                </Tooltip>
                            </IconButton>
                        )}
                        {props.mode !== Mode.View && !props.hiddenAction?.view && (
                            <IconButton
                                sx={{ p: 2 }}
                                onClick={(e) => {
                                    stopPropagation(e as any);
                                    props.onView();
                                }}
                            >
                                <Tooltip title={strings.Common.VIEW_DETAIL} placement="top">
                                    <Visibility sx={{ color: "#ffffff" }} />
                                </Tooltip>
                            </IconButton>
                        )}
                    </>
                )}
                {props.mode !== Mode.View && !props.hiddenAction?.remove && (
                    <IconButton
                        sx={{ p: 2 }}
                        onClick={(e) => {
                            stopPropagation(e as any);
                            handleRemove();
                        }}
                    >
                        <Tooltip title={strings.Common.DELETE} placement="top">
                            {uploading ? <ClearOutlined sx={{ color: "#ffffff" }} /> : <Delete sx={{ color: "#ffffff" }} />}
                        </Tooltip>
                    </IconButton>
                )}
            </Box>

            {/* Uploading progress */}
            <div style={{ position: "absolute", width: "100%", left: -1, bottom: 0 }}>
                {uploading && <LinearProgress className="w-100" variant="determinate" value={uploadProgress} color="info" />}
            </div>
        </Box>
    );
};

export default PreviewImageItem;
